import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';

import { CounselorNav } from '../counselor-nav/counselor-nav';
import { IssueReport, ReportCategory, ReportStatus } from '../../../../models/reports';
import { StatusStepper } from '../../../common/status-stepper/status-stepper';
import { IssueReportService } from '../../../../services/reports.service';
import { IssueReportsDialog } from '../../district-view/district-reports/reports-dialog/issue-reports-dialog';
import { IssueReportsInfo } from '../../district-view/district-reports/issue-reports-info/issue-reports-info';
import { HasPermissionDirective } from '../../../../security/has-permission.directive';
import { USER_PERMISSIONS } from '../../../../constants/permissions';

const CATEGORY_ICON: Record<ReportCategory, string> = {
  Bug: 'bug_report',
  Outage: 'flash_off',
  'Feature Request': 'lightbulb',
  'Access Issue': 'key',
  Other: 'chat_bubble',
};

const CATEGORY_CLASS: Record<ReportCategory, string> = {
  Bug: 'bug',
  Outage: 'outage',
  'Feature Request': 'feature',
  'Access Issue': 'access',
  Other: 'other',
};

const STATUS_CLASS: Record<ReportStatus, string> = {
  Submitted: 'submitted',
  Acknowledged: 'acknowledged',
  'In Progress': 'progress',
  Resolved: 'resolved',
};

type FilterValue = 'All' | 'Open' | 'Resolved';

/**
 * Faculty-facing help-desk view. Lists the reports the current faculty user has
 * submitted, mirroring the district reports page but rendered inside the
 * counselor shell. Gated by the `reports.view` permission at the route level.
 */
@Component({
  selector: 'app-counselor-reports',
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatChipsModule,
    MatFormFieldModule,
    MatInputModule,
    MatTooltipModule,
    MatDialogModule,
    CounselorNav,
    StatusStepper,
    HasPermissionDirective,
  ],
  templateUrl: './counselor-reports.html',
  styleUrl: './counselor-reports.css',
})
export class CounselorReports {
  reportService = inject(IssueReportService);
  dialog = inject(MatDialog);

  /** Permission constants exposed to the template. */
  permissions = USER_PERMISSIONS;

  reports = signal<IssueReport[]>([]);
  filter = signal<FilterValue>('All');
  query = signal('');

  openCount = computed(() => this.reports().filter((r) => r.status !== 'Resolved').length);

  inProgressCount = computed(() => this.reports().filter((r) => r.status === 'In Progress').length);

  resolvedCount = computed(() => this.reports().filter((r) => r.status === 'Resolved').length);

  filteredReports = computed(() => {
    const f = this.filter();
    const q = this.query().trim().toLowerCase();

    return this.reports().filter((r) => {
      const matchesFilter =
        f === 'All' ||
        (f === 'Open' && r.status !== 'Resolved') ||
        (f === 'Resolved' && r.status === 'Resolved');

      const matchesQuery = q === '' || r.title.toLowerCase().includes(q);

      return matchesFilter && matchesQuery;
    });
  });

  ngOnInit() {
    this.loadReports();
  }

  private loadReports(): void {
    this.reportService.getReports().subscribe((reports) => {
      this.reports.set(reports);
    });
  }

  setFilter(value: FilterValue): void {
    this.filter.set(value);
  }

  categoryIcon(category: ReportCategory): string {
    return CATEGORY_ICON[category];
  }

  categoryClass(category: ReportCategory): string {
    return CATEGORY_CLASS[category];
  }

  statusClass(status: ReportStatus): string {
    return STATUS_CLASS[status];
  }

  openReportDialog(report: IssueReport): void {
    const ref = this.dialog.open(IssueReportsInfo, {
      width: '640px',
      maxWidth: '95vw',
      data: {
        report,
        currentUserId: 1,
      },
      autoFocus: false,
    });

    let changed = false;
    ref.beforeClosed().subscribe(() => {
      changed = ref.componentInstance?.reportChanged ?? false;
    });
    ref.afterClosed().subscribe(() => {
      if (changed) {
        this.loadReports();
      }
    });
  }

  createReportDialog(): void {
    const ref = this.dialog.open(IssueReportsDialog, {
      width: '640px',
      maxWidth: '95vw',
      data: {},
      autoFocus: false,
    });
    ref.afterClosed().subscribe(() => this.loadReports());
  }
}
